import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Chip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, AccessTime, WbSunny, AcUnit } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { vortxColors, vortxTypography } from '../core/theme.js';
import { convertWindowsToGrid, convertGridToWindows } from '../utils/tariffTransformers.js';
import TOUHeatMapGrid from './TOUHeatMapGrid.jsx';

// Months const
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * TOUBuilder
 * Manages complex Time-of-Use rate structures with season support.
 * Data Model:
 * - seasons: [{ id: 0, name: 'Summer', months: [5,6,7,8], color: '#D4A84B' }, ...]
 * - periods: [{ id: 0, name: 'Off-Peak', energy: 0.1, demand: 0, seasonId: 0 }, ...]
 * - windows: [{ periodId: 1, startHour: 16, endHour: 21, months: [0..11], weekdays: true, weekends: false }]
 */
function TOUBuilder({
    touData,
    onChange,
    readOnly = false,
    onUnlock,
    tariffSource = 'custom',
    // Export compensation as render prop (optional - Site DNA passes this)
    renderExportCompensation,
    // Legacy props for backward compatibility
    exportConfig,
    onExportConfigChange,
    utilityCode,
    showExportCompensation = false,
}) {
    // Initialize seasons state - NO SILENT FALLBACKS, use empty arrays if no data
    const [seasons, setSeasons] = useState(touData?.seasons?.length > 0 ? touData.seasons : []);

    // Initialize periods - NO SILENT FALLBACKS
    const [periods, setPeriods] = useState(touData?.periods?.length > 0 ? touData.periods : []);

    // Initialize windows - NO SILENT FALLBACKS
    // If windows are missing but schedule exists (e.g. from ExtractionReview), hydrate from schedule
    const [windows, setWindows] = useState(() => {
        if (touData?.windows?.length > 0) return touData.windows;
        if (touData?.schedule && touData?.periods?.length > 0) {
            return convertGridToWindows(touData.schedule, touData.periods);
        }
        return [];
    });

    // Use refs to track current state values for comparison in sync effect
    // This avoids stale closure issues without adding local state to dependency array
    const seasonsRef = React.useRef(seasons);
    const periodsRef = React.useRef(periods);
    const windowsRef = React.useRef(windows);

    // Keep refs in sync with state
    seasonsRef.current = seasons;
    periodsRef.current = periods;
    windowsRef.current = windows;

    // Sync state when touData prop changes (e.g., loading a new tariff)
    // Only runs when touData changes, not when local state changes
    useEffect(() => {
        if (touData?.seasons?.length > 0) {
            // Compare by stringifying to detect actual changes, not just reference changes
            const incomingSeasons = JSON.stringify(touData.seasons);
            const currentSeasons = JSON.stringify(seasonsRef.current);
            if (incomingSeasons !== currentSeasons) {
                setSeasons(touData.seasons);
            }
        }
        if (touData?.periods?.length > 0) {
            const incomingPeriods = JSON.stringify(touData.periods);
            const currentPeriods = JSON.stringify(periodsRef.current);
            if (incomingPeriods !== currentPeriods) {
                setPeriods(touData.periods);
            }
        }
        if (touData?.windows?.length > 0) {
            const incomingWindows = JSON.stringify(touData.windows);
            const currentWindows = JSON.stringify(windowsRef.current);
            if (incomingWindows !== currentWindows) {
                setWindows(touData.windows);
            }
        } else if (touData?.schedule && touData?.periods?.length > 0) {
            // Hydrate windows from schedule grid when windows are empty
            // (e.g., approved tariffs stored with schedule_json but no windows)
            const hydrated = convertGridToWindows(touData.schedule, touData.periods);
            if (hydrated.length > 0) {
                const incomingWindows = JSON.stringify(hydrated);
                const currentWindows = JSON.stringify(windowsRef.current);
                if (incomingWindows !== currentWindows) {
                    setWindows(hydrated);
                }
            }
        }
    }, [touData?.seasons, touData?.periods, touData?.windows, touData?.schedule]);

    // Update parent on change - include seasons and explicitly computed scheduleGrid
    useEffect(() => {
        // We re-compute the grid here to ensure the parent gets the full matrix representation
        const currentGrid = convertWindowsToGrid(windows, periods);
        // Preserve fixed charges from touData prop
        onChange({
            seasons,
            periods,
            windows,
            schedule: currentGrid,
            fixedMonthlyFee: touData?.fixedMonthlyFee,
            facilityDemand: touData?.facilityDemand
        });
    }, [seasons, periods, windows]);

    // --- Season Management ---
    const updateSeason = (seasonId, field, value) => {
        const newSeasons = seasons.map(s =>
            s.id === seasonId ? { ...s, [field]: value } : s
        );
        setSeasons(newSeasons);
    };

    const toggleSeasonMonth = (seasonId, monthIdx) => {
        // Get current season
        const season = seasons.find(s => s.id === seasonId);
        if (!season) return;

        const newSeasons = seasons.map(s => {
            if (s.id === seasonId) {
                // Add month to this season if not already there
                if (!s.months.includes(monthIdx)) {
                    return { ...s, months: [...s.months, monthIdx].sort((a, b) => a - b) };
                }
                // Don't allow removing if it's the only month
                if (s.months.length > 1) {
                    return { ...s, months: s.months.filter(m => m !== monthIdx) };
                }
            } else if (s.months.includes(monthIdx)) {
                // Remove month from other season (mutual exclusivity)
                return { ...s, months: s.months.filter(m => m !== monthIdx) };
            }
            return s;
        });
        setSeasons(newSeasons);
    };

    const addSeason = () => {
        const newId = seasons.length > 0 ? Math.max(...seasons.map(s => s.id)) + 1 : 0;
        // Find unassigned months
        const assignedMonths = seasons.flatMap(s => s.months);
        const unassignedMonths = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].filter(m => !assignedMonths.includes(m));

        setSeasons([...seasons, {
            id: newId,
            name: `Season ${newId + 1}`,
            months: unassignedMonths.length > 0 ? [unassignedMonths[0]] : [],
            color: ['#27ae60', '#9b59b6', '#3498db'][newId % 3] // Rotate through colors
        }]);
    };

    const removeSeason = (seasonId) => {
        // Don't remove if only 1 season left
        if (seasons.length <= 1) return;

        // Get months from removed season and assign to first remaining season
        const removedSeason = seasons.find(s => s.id === seasonId);
        const remainingSeasons = seasons.filter(s => s.id !== seasonId);

        if (removedSeason?.months?.length > 0 && remainingSeasons.length > 0) {
            remainingSeasons[0].months = [...remainingSeasons[0].months, ...removedSeason.months].sort((a, b) => a - b);
        }

        setSeasons(remainingSeasons);

        // Update periods that referenced removed season to null (all-season)
        setPeriods(periods.map(p => p.seasonId === seasonId ? { ...p, seasonId: null } : p));
    };

    // --- Period Management ---
    // Use functional setState to ensure we always have the latest state
    // This is critical when updatePeriod is called multiple times in one event handler
    const updatePeriod = (index, field, value) => {
        setPeriods(currentPeriods => {
            const newPeriods = [...currentPeriods];
            newPeriods[index] = { ...newPeriods[index], [field]: value };
            return newPeriods;
        });
    };

    const addPeriod = (seasonId = null) => {
        const newId = periods.length > 0 ? Math.max(...periods.map(p => p.id)) + 1 : 0;
        setPeriods([...periods, { id: newId, name: `Period ${newId + 1}`, energy: '', demand: '', seasonId }]);
    };

    const removePeriod = (index) => {
        // Prevent removing base period 0
        if (index === 0) return;
        const newPeriods = [...periods];
        newPeriods.splice(index, 1);
        setPeriods(newPeriods);
    };

    // --- Window Management ---
    const addWindow = () => {
        setWindows([...windows, {
            id: Date.now(),
            periodId: periods[1]?.id || 0,
            startHour: 12,
            endHour: 18,
            months: [5, 6, 7, 8], // Default to Summer
            weekdays: true,
            weekends: false
        }]);
    };

    const removeWindow = (index) => {
        const newWindows = [...windows];
        newWindows.splice(index, 1);
        setWindows(newWindows);
    };

    const updateWindow = (index, field, value) => {
        const newWindows = [...windows];
        newWindows[index] = { ...newWindows[index], [field]: value };
        setWindows(newWindows);
    };

    const toggleMonth = (windowIndex, monthIndex) => {
        const window = windows[windowIndex];
        const newMonths = window.months.includes(monthIndex)
            ? window.months.filter(m => m !== monthIndex)
            : [...window.months, monthIndex].sort((a, b) => a - b);
        updateWindow(windowIndex, 'months', newMonths);
    };

    // Memoized grid state derived from windows
    const scheduleGrid = useMemo(() =>
        convertWindowsToGrid(windows, periods),
        [windows, periods]
    );

    // Handle grid changes from heat map
    const handleGridChange = useCallback((newSchedule) => {
        const newWindows = convertGridToWindows(newSchedule, periods);
        setWindows(newWindows);
    }, [periods]);

    return (
        <Box sx={{ mt: 2 }}>
            {readOnly && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: alpha(vortxColors.gold, 0.1), borderRadius: 1, border: `1px solid ${alpha(vortxColors.gold, 0.4)}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: vortxColors.gold, display: 'flex', alignItems: 'center', gap: 1 }}>
                            Loaded from Flux Rate Database.
                            Click "Unlock for Editing" to make adjustments.
                        </Typography>
                        {onUnlock && (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={onUnlock}
                                sx={{ borderColor: vortxColors.gold, color: vortxColors.gold, fontSize: '0.7rem' }}
                            >
                                Unlock for Editing
                            </Button>
                        )}
                    </Box>
                </Box>
            )}
            <Typography variant="body2" color="text.secondary" paragraph>
                {readOnly
                    ? ''
                    : 'Configure seasons, define rate periods for each season, then paint the schedule grid.'}
            </Typography>

            {/* 1. Fixed Charges Section */}
            <Box sx={{ mb: 3, p: 2, bgcolor: alpha(vortxColors.gold, 0.05), borderRadius: 1, border: `1px solid ${alpha(vortxColors.gold, 0.2)}` }}>
                <Typography variant="subtitle2" sx={{ color: vortxColors.gold, mb: 2 }}>1. Fixed Charges</Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <TextField
                        label="Fixed Monthly Fee ($/month)"
                        type="number"
                        size="small"
                        value={touData?.fixedMonthlyFee || ''}
                        onChange={(e) => onChange({ ...touData, seasons, periods, windows, fixedMonthlyFee: e.target.value })}
                        helperText="Customer charge / service fee applied monthly."
                        sx={{ width: 280 }}
                        InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>$</Typography>, readOnly }}
                        disabled={readOnly}
                    />
                </Box>
            </Box>

            {/* Export Compensation (optional - render prop from parent) */}
            {renderExportCompensation && renderExportCompensation()}

            {/* 2. Season Definitions */}
            <Box sx={{ mb: 3, p: 2, bgcolor: alpha(vortxColors.gold, 0.05), borderRadius: 1, border: `1px solid ${alpha(vortxColors.gold, 0.2)}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: vortxColors.gold }}>
                        2. Define Seasons
                    </Typography>
                    {!readOnly && (
                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={addSeason}
                            sx={{ fontSize: '0.75rem' }}
                        >
                            Add Season
                        </Button>
                    )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Click on months to assign them to each season. Each month belongs to exactly one season.
                </Typography>

                {/* Season Cards */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {seasons.map((season, idx) => (
                        <Box
                            key={season.id}
                            sx={{
                                p: 2,
                                bgcolor: alpha(season.color || '#666', 0.1),
                                borderRadius: 1,
                                border: `2px solid ${season.color || '#666'}`
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                {/* Season Icon */}
                                <Box sx={{ color: season.color }}>
                                    {season.id === 0 ? <WbSunny /> : season.id === 1 ? <AcUnit /> : <AccessTime />}
                                </Box>

                                {/* Season Name */}
                                <TextField
                                    size="small"
                                    value={season.name}
                                    onChange={(e) => updateSeason(season.id, 'name', e.target.value)}
                                    disabled={readOnly}
                                    sx={{ width: 150 }}
                                    InputProps={{
                                        sx: { fontWeight: 600, color: season.color },
                                        readOnly
                                    }}
                                />

                                {/* Month count */}
                                <Chip
                                    label={`${season.months.length} months`}
                                    size="small"
                                    sx={{ bgcolor: alpha(season.color || '#666', 0.2) }}
                                />

                                {/* Remove button */}
                                {!readOnly && seasons.length > 1 && (
                                    <IconButton
                                        size="small"
                                        onClick={() => removeSeason(season.id)}
                                        sx={{ ml: 'auto' }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>

                            {/* Month Selectors */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {MONTHS.map((monthName, monthIdx) => {
                                    const isInSeason = season.months.includes(monthIdx);
                                    const isDisabled = readOnly || (isInSeason && season.months.length === 1);
                                    return (
                                        <Chip
                                            key={monthIdx}
                                            label={monthName}
                                            size="small"
                                            onClick={() => !readOnly && toggleSeasonMonth(season.id, monthIdx)}
                                            sx={{
                                                bgcolor: isInSeason ? season.color : 'transparent',
                                                color: isInSeason ? '#fff' : 'text.secondary',
                                                border: isInSeason ? 'none' : '1px dashed rgba(255,255,255,0.3)',
                                                cursor: isDisabled ? 'default' : 'pointer',
                                                opacity: isDisabled ? 0.7 : 1,
                                                '&:hover': isDisabled ? {} : {
                                                    bgcolor: isInSeason ? alpha(season.color, 0.8) : alpha(season.color, 0.3)
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* 3. Periods Definition - Grouped by Season */}
            <Typography variant="subtitle2" sx={{ color: vortxColors.gold, mb: 1 }}>3. Define Rate Periods</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Define energy and demand rates for each period. Periods with "All Seasons" apply year-round.
            </Typography>

            {/* Render periods grouped by season */}
            {
                (() => {
                    // Get periods for each season + all-season periods
                    const allSeasonPeriods = periods.filter(p => p.seasonId === null || p.seasonId === undefined);

                    // Helper to render a period table for a season
                    const renderPeriodTable = (periodList, season, isAllSeason = false) => {
                        const title = isAllSeason ? 'All Seasons (Base)' : `${season.id === 0 ? '' : season.id === 1 ? '' : ''} ${season.name}`;
                        const seasonColor = isAllSeason ? '#888888' : (season.color || '#666666');  // Use hex for alpha()
                        const textColor = isAllSeason ? 'text.secondary' : season.color;  // Use theme ref for typography
                        const seasonId = isAllSeason ? null : season.id;

                        return (
                            <Box key={isAllSeason ? 'all' : season.id} sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: textColor,
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 1
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                    {!readOnly && (
                                        <Button
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => addPeriod(seasonId)}
                                            sx={{ fontSize: '0.7rem' }}
                                        >
                                            Add {isAllSeason ? 'Base ' : ''}Period
                                        </Button>
                                    )}
                                </Box>
                                {periodList.length > 0 || isAllSeason ? (
                                    <Table size="small" sx={{ bgcolor: 'background.paper', borderRadius: 1, border: `1px solid ${alpha(seasonColor || '#666', 0.3)}` }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Period Name</TableCell>
                                                <TableCell sx={{ fontSize: '0.75rem' }}>Delivery ($/kWh)</TableCell>
                                                <TableCell sx={{ fontSize: '0.75rem' }}>Generation ($/kWh)</TableCell>
                                                <TableCell sx={{ fontSize: '0.75rem' }}>Total ($/kWh)</TableCell>
                                                <TableCell>Demand ($/kW)</TableCell>
                                                <TableCell width={50}></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {periodList.map((period) => {
                                                const index = periods.findIndex(p => p.id === period.id);
                                                // Calculate total from delivery + generation
                                                const delivery = parseFloat(period.deliveryEnergy) || 0;
                                                const generation = parseFloat(period.generationEnergy) || 0;
                                                const total = delivery + generation;
                                                return (
                                                    <TableRow key={period.id}>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={period.name}
                                                                onChange={(e) => updatePeriod(index, 'name', e.target.value)}
                                                                disabled={readOnly}
                                                                InputProps={{ readOnly }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                value={period.deliveryEnergy || ''}
                                                                onChange={(e) => {
                                                                    const newDelivery = parseFloat(e.target.value) || 0;
                                                                    const newTotal = newDelivery + (parseFloat(period.generationEnergy) || 0);
                                                                    updatePeriod(index, 'deliveryEnergy', e.target.value);
                                                                    updatePeriod(index, 'energy', newTotal.toFixed(5));
                                                                }}
                                                                InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>$</Typography>, readOnly }}
                                                                disabled={readOnly}
                                                                sx={{ width: 100 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                value={period.generationEnergy || ''}
                                                                onChange={(e) => {
                                                                    const newGeneration = parseFloat(e.target.value) || 0;
                                                                    const newTotal = (parseFloat(period.deliveryEnergy) || 0) + newGeneration;
                                                                    updatePeriod(index, 'generationEnergy', e.target.value);
                                                                    updatePeriod(index, 'energy', newTotal.toFixed(5));
                                                                }}
                                                                InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>$</Typography>, readOnly }}
                                                                disabled={readOnly}
                                                                sx={{ width: 100 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: vortxColors.gold,
                                                                    fontFamily: vortxTypography.mono
                                                                }}
                                                            >
                                                                ${total > 0 ? total.toFixed(5) : (period.energy || '0')}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                value={period.demand || ''}
                                                                onChange={(e) => updatePeriod(index, 'demand', e.target.value)}
                                                                InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>$</Typography>, readOnly }}
                                                                disabled={readOnly}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {!readOnly && index > 0 && (
                                                                <IconButton size="small" onClick={() => removePeriod(index)}>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            {/* Facility Demand row - only in All Seasons section */}
                                            {isAllSeason && (
                                                <TableRow>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value="Facility Demand"
                                                            disabled
                                                            InputProps={{ readOnly: true }}
                                                            helperText="Non-coincident peak"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">—</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">—</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">—</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={touData?.facilityDemand || ''}
                                                            onChange={(e) => onChange({ ...touData, seasons, periods, windows, facilityDemand: e.target.value })}
                                                            InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>$</Typography>, readOnly }}
                                                            disabled={readOnly}
                                                        />
                                                    </TableCell>
                                                    <TableCell></TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        No periods defined for this season. Click "Add Period" to create one.
                                    </Typography>
                                )}
                            </Box>
                        );
                    };

                    return (
                        <>
                            {/* All-season (base) periods first */}
                            {renderPeriodTable(allSeasonPeriods, null, true)}

                            {/* Then each season's periods */}
                            {seasons.map(season => {
                                const seasonPeriods = periods.filter(p => p.seasonId === season.id);
                                return renderPeriodTable(seasonPeriods, season, false);
                            })}
                        </>
                    );
                })()
            }

            {/* 3. Schedule Heat Map */}
            <Typography variant="subtitle2" sx={{ color: vortxColors.gold, mb: 1 }}>4. Assign Schedule Windows</Typography>
            <Box sx={{ overflowX: 'auto', pb: 1 }}>
                <TOUHeatMapGrid
                    periods={periods}
                    seasons={seasons}
                    schedule={scheduleGrid}
                    onChange={handleGridChange}
                    readOnly={readOnly}
                />
            </Box>
        </Box >
    );
}

export default TOUBuilder;
